import localforage from 'localforage';

export type QueuedUpload = {
    id: string;
    ownerId?: string;
    situationId: string;
    file: File;
    type: any;
    conditionCategory?: string;
    latitude: number | null;
    longitude: number | null;
    evidenceId?: string;
    evidenceFolderId?: string;
};
export type UploadGroup = {
    type: string | null;
    data: QueuedUpload[];
    folder?: any;
    replacementId?: string;
};
export type UploadQueue = Record<string, UploadGroup>;

// Serialize read/modify/write transactions, including between tabs where Web Locks exists.
const tails: Record<string, Promise<any>> = {};
export const withUploadLock = <T>(name: string, action: () => Promise<T>): Promise<T> => {
    const { locks } = navigator as any;
    if (locks) return locks.request(`proofkeep-${name}`, action);
    const next = (tails[name] || Promise.resolve()).then(action, action);
    tails[name] = next.catch(() => undefined);
    return next;
};
export const newUploadId = () => {
    const bytes = new Uint8Array(16);
    crypto.getRandomValues(bytes);
    bytes[6] = (bytes[6] & 15) | 64;
    bytes[8] = (bytes[8] & 63) | 128;
    const hex = Array.from(bytes, (byte) => byte.toString(16).padStart(2, '0')).join('');
    return `${hex.slice(0, 8)}-${hex.slice(8, 12)}-${hex.slice(12, 16)}-${hex.slice(
        16,
        20
    )}-${hex.slice(20)}`;
};

export const changeQueue = <T>(change: (queue: UploadQueue) => T | Promise<T>): Promise<T> =>
    withUploadLock('upload-queue', async () => {
        const queue = (await localforage.getItem<UploadQueue>('evidences')) || {};
        // Migrate existing recordings in place. Never discard legacy blobs.
        Object.values(queue).forEach((group) =>
            group.data.forEach((item) => {
                if (!item.id) item.id = newUploadId();
            })
        );
        const result = await change(queue);
        await localforage.setItem('evidences', queue);
        return result;
    });

export const readQueue = (): Promise<UploadQueue> =>
    withUploadLock('upload-queue', async () => {
        const queue = (await localforage.getItem<UploadQueue>('evidences')) || {};
        let migrated = false;
        Object.values(queue).forEach((group) =>
            group.data.forEach((item) => {
                if (!item.id) {
                    item.id = newUploadId();
                    migrated = true;
                }
            })
        );
        if (migrated) await localforage.setItem('evidences', queue);
        return queue;
    });
export const countQueue = (queue: UploadQueue) =>
    Object.values(queue).reduce((count, group) => count + group.data.length, 0);
export const rememberCaptureFolder = (folderId: string, folder: any) =>
    withUploadLock('capture-folders', async () => {
        const folders = (await localforage.getItem<Record<string, any>>('captureFolders')) || {};
        folders[folderId] = folder;
        await localforage.setItem('captureFolders', folders);
    });
export const enqueueUpload = (
    folderId: string,
    type: string | null,
    item: QueuedUpload,
    folder?: any
) =>
    changeQueue(async (queue) => {
        const folders = (await localforage.getItem<Record<string, any>>('captureFolders')) || {};
        const group = queue[folderId] || (queue[folderId] = { type, data: [] });
        if (folder || folders[folderId]) group.folder = folder || folders[folderId];
        if (!group.data.some((entry) => entry.id === item.id)) group.data.push(item);
        return item;
    });
export const patchUpload = (folderId: string, id: string, patch: Partial<QueuedUpload>) =>
    changeQueue((queue) => {
        const item = queue[folderId]?.data.find((entry) => entry.id === id);
        if (item) Object.assign(item, patch);
    });
export const removeUpload = (folderId: string, id: string) =>
    changeQueue((queue) => {
        const group = queue[folderId];
        if (!group) return;
        group.data = group.data.filter((item) => item.id !== id);
        if (!group.data.length) delete queue[folderId];
    });

// Recreate only a confirmed missing/expired destination, never on network/auth/server errors.
// The original queue group remains the durable key until every file is acknowledged.
export const resolveUploadFolder = async (
    folderId: string,
    getFolder: (id: string) => Promise<any>,
    createFolder: (data: any) => Promise<{ situationId: string }>,
    fallbackSubtype: string
) => {
    const group = (await readQueue())[folderId];
    if (!group) throw new Error('The locally saved upload could not be found.');
    const destination = group.replacementId || folderId;
    let { folder } = group;
    try {
        folder = await getFolder(destination);
        await changeQueue((queue) => {
            if (queue[folderId]) queue[folderId].folder = folder;
        });
        if (!folder.expired && Date.parse(folder.expirationDate) > Date.now()) {
            return { folderId: destination, subtype: folder.subtype || folder.type };
        }
    } catch (error: any) {
        if (error?.status !== 404) throw error;
    }
    const candidate = folder?.subtype || folder?.type;
    const subtype = [
        'apartment',
        'hotel',
        'house',
        'car',
        'custom_capture',
        'future_gram',
    ].includes(candidate)
        ? candidate
        : fallbackSubtype;
    if (subtype === 'hotel' && !(folder?.roomNumber || folder?.room_number)) {
        throw new Error(
            'The hotel room details are missing. Your files remain saved on this device.'
        );
    }
    const payload = {
        name: folder?.name || 'Recovered captures',
        subtype,
        type: subtype,
        details: folder?.details || 'evidence',
        info: folder?.info || 'evidence',
        address: folder?.address || 'unknown',
        city: folder?.city || 'unknown',
        ...(subtype === 'hotel' ? { room_number: folder.roomNumber || folder.room_number } : {}),
    };
    const { situationId } = await createFolder(payload);
    await changeQueue((queue) => {
        if (!queue[folderId]) throw new Error('Upload queue changed during recovery.');
        queue[folderId].replacementId = situationId;
        queue[folderId].folder = payload;
    });
    return { folderId: situationId, subtype, recovered: true };
};
